import { StyledEngineProvider } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import React, {
	lazy, Suspense, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useLocation } from 'react-router';
import { Redirect } from 'react-router-dom';
import * as actions from 'store/actions/index';
import { getIsMobile } from 'store/selectors/ui';
import { componentLoader } from 'utils/utils';
import useCustomConfig from 'hooks/useConfig';
import { getAdminStatus, getPublisherStatus, checkMaintenanceMode } from 'api/adminUtils';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ToastStyles.css'; // Custom mobile-friendly toast styles
import MaintenanceDialog from 'components/Dialogs/MaintenanceDialog';
import { embed } from 'router/urlQueryFields';
import LoadingSpinner from 'components/Common/LoadingSpinner';
import ProtectedRoute from 'components/Common/ProtectedRoute';
import { DEFAULT_THEME, ROUTES, LOADING_MESSAGES, TOAST_CONFIG_DESKTOP, TOAST_CONFIG_MOBILE } from './AppConfig';
import { createAppTheme } from 'theme/createAppTheme';

// Lazy-loaded components for code splitting and performance optimization
const Dashboard = lazy(() => componentLoader(() => import('components/Dashboard/Dashboard')));
const LegalDisclaimer = lazy(() => componentLoader(() => import('components/Disclaimers/Legal')));
const PrivacyDisclaimer = lazy(() => componentLoader(() => import('components/Disclaimers/Privacy')));
const Login = lazy(() => componentLoader(() => import('components/Login/Login')));
const AdminPage = lazy(() => componentLoader(() => import('components/Admin/Admin')));
const PublishPage = lazy(() => componentLoader(() => import('components/Publish/Publish')));
const ShortUrlRedirect = lazy(() => componentLoader(() => import('../../router/ShortUrlRedirect')));
const SuitabilityMapping = lazy(() => componentLoader(() => import('components/Suitability/SuitabilityMapping')));

/**
 * App Component - Root application component
 *
 * Responsibilities:
 * - Application routing and navigation
 * - Theme configuration and provider setup
 * - Authentication and authorization flow
 * - Maintenance mode management
 * - Global state initialization
 * - Responsive layout detection
 *
 * @returns {JSX.Element} The root application component
 */
function App() {
	const dispatch = useDispatch();
	const isSignedIn = useSelector((state) => state.user.get('authenticated'));
	const isMobile = useSelector(getIsMobile);
	const config = useCustomConfig();

	const location = useLocation();
	// Store original URL parameters for redirect after login
	const [originalUrlParameter, setOriginalUrlParameters] = useState('');

	// Maintenance mode state management
	// - isEnabled: Whether to display the maintenance dialog to users
	// - shouldBlock: Whether users can close the dialog (true = blocking mode)
	// - loading: Whether we're still fetching maintenance status from server
	const [maintenanceMode, setMaintenanceMode] = useState({
		isEnabled: false,
		shouldBlock: false,
		loading: true,
	});

	// Extract theme colors from config or use defaults
	const mainColor = config?.ui?.primaryColor ?? DEFAULT_THEME.primaryColor;
	const secondaryColor = config?.ui?.secondaryColor ?? DEFAULT_THEME.secondaryColor;

	// Initialize app on mount: save URL params and load available languages
	useEffect(() => {
		setOriginalUrlParameters(location.search);
		dispatch(actions.loadAvailableLanguages());
	}, [location.search, dispatch]);

	// Check maintenance mode on app load
	// Fetches maintenance status from server when config is available
	// Falls back to disabled state if there's an error
	useEffect(() => {
		const checkMaintenance = async () => {
			try {
				const maintenanceStatus = await checkMaintenanceMode();
				if (maintenanceStatus?.maintenanceMode) {
					setMaintenanceMode({
						isEnabled: maintenanceStatus.maintenanceMode.isEnabled || false,
						shouldBlock: maintenanceStatus.maintenanceMode.shouldBlock || false,
						loading: false,
					});
				}
			} catch (error) {
				console.error('Error checking maintenance mode:', error);
				// On error, disable maintenance mode and allow app access
				setMaintenanceMode({
					isEnabled: false,
					shouldBlock: false,
					loading: false,
				});
			}
		};

		if (config !== null) {
			checkMaintenance();
		}
	}, [config]);

	// Check if embed parameter is present for anonymous sign-in flow
	const anonymousSignIn = originalUrlParameter.includes(embed);

	/**
	 * rootRedirect - Determines the root path redirect based on config and auth status
	 * - Shows empty div while config is loading
	 * - Redirects to Dashboard if signed in
	 * - Redirects to Login if not signed in (preserving URL parameters)
	 */
	const rootRedirect = () => {
		if (config === null) {
			return (<div />);
		} if (isSignedIn) {
			return (<Dashboard />);
		}

		// Don't redirect if we're checking auth state - show loading instead
		// This prevents losing URL parameters during auth check
		if (isSignedIn === undefined) {
			return (<div>Loading...</div>);
		}

		// Preserve URL parameters when redirecting to login
		return (<Redirect to={{
			pathname: ROUTES.LOGIN,
			search: location.search
		}} />);
	};


	/**
	 * SuitabilityRoute - Conditionally renders suitability mapping feature
	 * Only accessible when the suitabilityMapping feature flag is enabled
	 */
	function SuitabilityRoute() {
		if (config?.featureFlags?.suitabilityMapping) {
			return <SuitabilityMapping />;
		}
		return <Redirect to={ROUTES.ROOT} />;
	}

	// Material UI Theme - Memoized to prevent unnecessary re-renders
	const theme = React.useMemo(
		() => createAppTheme(mainColor, secondaryColor),
		[mainColor, secondaryColor],
	);

	/**
	 * Global Window Resize Handler
	 * Detects viewport size changes and updates Redux state with mobile/desktop status
	 * Breakpoint: Material-UI 'md' (typically 960px)
	 */
	useEffect(() => {
		const handleResize = () => {
			const isMobile = window.innerWidth <= theme.breakpoints.values.md;
			dispatch(actions.setIsMobile(isMobile));
		};

		// Set initial value
		handleResize();

		// Listen for resize events
		window.addEventListener('resize', handleResize);

		// Cleanup listener on unmount
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [dispatch, theme.breakpoints.values.md]);

	// Show loading screen while checking maintenance mode
	if (maintenanceMode.loading) {
		return <LoadingSpinner fullScreen message={LOADING_MESSAGES.APP} />;
	}

	return (
		<StyledEngineProvider injectFirst>
			<ThemeProvider theme={theme}>
				<ToastContainer {...(isMobile ? TOAST_CONFIG_MOBILE : TOAST_CONFIG_DESKTOP)} />
				
				{/* Maintenance Mode Dialog */}
				<MaintenanceDialog
					open={maintenanceMode.isEnabled}
					onClose={ () => setMaintenanceMode({ ...maintenanceMode, isEnabled: false })}
					allowClose={!maintenanceMode.shouldBlock}
				/>

				<Suspense fallback={<div />}>
					<Switch>
						<Route path={ROUTES.LEGAL} component={LegalDisclaimer} />
						<Route path={ROUTES.PRIVACY} component={PrivacyDisclaimer} />
						<Route path={ROUTES.SHORT_URL} component={ShortUrlRedirect} />
						<Route
							path={ROUTES.LOGIN}
							render={() => {
								return !isSignedIn && config !== null
									? <Login anonymous={anonymousSignIn} />
									: (
										<Redirect to={{
											pathname: ROUTES.ROOT,
											search: originalUrlParameter || location.search,
										}}
										/>
									);
							}}
						/>
						<Route
							path={ROUTES.ADMIN}
							render={() => (
								<ProtectedRoute
									isSignedIn={isSignedIn}
									checkPermission={getAdminStatus}
									component={AdminPage}
									loadingMessage={LOADING_MESSAGES.ADMIN_ACCESS}
								/>
							)}
						/>
						<Route
							path={ROUTES.PUBLISH}
							render={() => (
								<ProtectedRoute
									isSignedIn={isSignedIn}
									checkPermission={getPublisherStatus}
									component={PublishPage}
									loadingMessage={LOADING_MESSAGES.PUBLISHER_ACCESS}
								/>
							)}
						/>
						<Route path={ROUTES.SUITABILITY} component={SuitabilityRoute} />
						<Route path={ROUTES.ROOT} render={rootRedirect} />
					</Switch>
				</Suspense>
			</ThemeProvider>
		</StyledEngineProvider>
	);
}

export default App;